import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import PageHero, { HeroCtas } from '../components/ui/PageHero';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { SectionHead } from '../components/ui/SectionHead';
import DataTable from '../components/ui/DataTable';
import Notice from '../components/ui/Notice';
import StageList from '../components/ui/StageList';
import Button from '../components/ui/Button';
import Faq from '../components/ui/Faq';
import StatusChip from '../components/ui/StatusChip';
import { breadcrumbSchema } from '../components/ui/schema';

const GATE_INTRO_TERMS = ['Evidence quality', 'source and a date', 'basis is missing'];

const emphasize = (text, terms) => {
  const escaped = terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(${escaped.join('|')})`, 'g');
  return text
    .split(re)
    .map((part, i) =>
      terms.includes(part) ? (
        <strong key={i}>{part}</strong>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );
};

const Approach = () => {
  const { t } = useTranslation('cy');
  const meta = t('approach.meta', { returnObjects: true });

  const schema = [
    breadcrumbSchema([{ label: 'Home', to: '/' }, { label: 'How We Invest', to: '/approach' }]),
  ];

  return (
    <Layout>
      <SEO title={meta.title} description={meta.description} path="/approach" schema={schema} />

      <PageHero
        id="approach-hero"
        eyebrow={t('approach.hero.eyebrow')}
        title={t('approach.hero.h1')}
        lead={t('approach.hero.lead')}
        breadcrumb={<Breadcrumbs items={[{ label: t('nav.howWeInvest') }]} />}
      >
        <nav aria-label="On this page" style={{ marginTop: 24 }}>
          <ol style={{ listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 16, margin: 0, padding: 0 }}>
            {t('approach.hero.stages', { returnObjects: true }).map((s, i) => (
              <li key={s}>
                <a className="cy-link" href="#approach-stages">
                  {i + 1}. {s}
                </a>
              </li>
            ))}
          </ol>
        </nav>
        <p className="cy-body" style={{ marginTop: 24 }}>{t('approach.hero.boundary')}</p>
        <HeroCtas
          primary={t('approach.hero.cta')}
          primaryTo="/submit-opportunity"
          secondary={t('approach.hero.discuss')}
          secondaryTo="/contact?topic=partnership"
        />
      </PageHero>

      {/* approach-gates */}
      <section className="cy-section cy-section--alt" id="approach-gates">
        <div className="cy-container">
          <SectionHead title={t('approach.gates.h2')} lead={t('approach.gates.lead')} />
          <p className="cy-gate-intro">{emphasize(t('approach.gates.intro'), GATE_INTRO_TERMS)}</p>
          <p className="cy-fineprint" style={{ marginTop: 16 }}>{t('approach.gates.lens')}</p>
          <DataTable
            columns={[
              { key: 'gate', label: 'Gate' },
              { key: 'evidence', label: 'Pass signal' },
              { key: 'stop', label: 'Stop signal' },
            ]}
            rows={t('approach.gates.rows', { returnObjects: true })}
          />
          <p style={{ marginTop: 24, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {t('approach.gates.links', { returnObjects: true }).map((l) => (
              <Link className="cy-link" key={l.to} to={l.to}>{l.label}</Link>
            ))}
          </p>
        </div>
      </section>

      {/* approach-stages */}
      <section className="cy-section" id="approach-stages">
        <div className="cy-container">
          <SectionHead title={t('approach.stages.h2')} lead={t('approach.stages.lead')} />
          <StageList items={t('approach.stages.items', { returnObjects: true })} />
          <Notice variant="inline" style={{ marginTop: 24 }}>
            {t('approach.stages.risk')}{' '}
            <Link className="cy-link" to="#approach-risk">Risk discipline</Link>
          </Notice>
        </div>
      </section>

      {/* approach-roles */}
      <section className="cy-section cy-section--alt" id="approach-roles">
        <div className="cy-container">
          <SectionHead title={t('approach.roles.h2')} lead={t('approach.roles.lead')} />
          <DataTable
            columns={[
              { key: 'party', label: 'Party' },
              { key: 'responsible', label: 'Usually responsible for' },
              { key: 'decisions', label: 'Decisions' },
              { key: 'notResponsible', label: 'Not responsible for' },
            ]}
            rows={t('approach.roles.rows', { returnObjects: true })}
          />
          <p className="cy-fineprint" style={{ marginTop: 24 }}>{t('approach.roles.note')}</p>
        </div>
      </section>

      {/* approach-capabilities */}
      <section className="cy-section" id="approach-capabilities">
        <div className="cy-container">
          <SectionHead title={t('approach.capabilities.h2')} lead={t('approach.capabilities.lead')} />
          <DataTable
            columns={[
              { key: 'cap', label: 'Capability' },
              { key: 'behaviour', label: 'Observable behaviour' },
              { key: 'evidence', label: 'Allowed evidence' },
              { key: 'forbidden', label: 'Not accepted' },
            ]}
            rows={t('approach.capabilities.rows', { returnObjects: true })}
          />
          <div style={{ marginTop: 24, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <StatusChip variant="pending">Evidence pending</StatusChip>
            <span className="cy-fineprint">{t('approach.capabilities.note')}</span>
          </div>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/about#about-team">{t('approach.capabilities.cta')}</Link>
            {' · '}
            <Link className="cy-link" to="#approach-gates">{t('approach.capabilities.gateLink')}</Link>
          </p>
        </div>
      </section>

      {/* approach-risk */}
      <section className="cy-section cy-section--alt" id="approach-risk">
        <div className="cy-container">
          <SectionHead eyebrow={t('approach.risk.eyebrow')} title={t('approach.risk.h2')} lead={t('approach.risk.lead')} />
          <DataTable
            columns={[
              { key: 'risk', label: 'Risk category' },
              { key: 'control', label: 'Control' },
              { key: 'escalate', label: 'Escalate / stop' },
            ]}
            rows={t('approach.risk.rows', { returnObjects: true })}
          />
          <Notice variant="compliance" style={{ marginTop: 24 }}>
            {t('approach.risk.principle')}{' '}
            <Link className="cy-link" to="/terms#terms-investment-boundary">{t('approach.risk.link')}</Link>
          </Notice>
        </div>
      </section>

      {/* approach-faq */}
      <section className="cy-section" id="approach-faq">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('approach.faq.h2')} lead={t('approach.faq.lead')} />
          <Faq items={t('approach.faq.items', { returnObjects: true })} idPrefix="approach-faq" />
          <div style={{ marginTop: 32, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
            <Button to="/submit-opportunity">{t('approach.faq.cta')}</Button>
            <Link className="cy-link" to="/contact">{t('approach.faq.alt')}</Link>
          </div>
        </div>
      </section>

      {/* approach-by-pillar */}
      <section className="cy-section cy-section--alt" id="approach-by-pillar">
        <div className="cy-container">
          <SectionHead title={t('approach.byPillar.h2')} lead={t('approach.byPillar.lead')} />
          <div className="cy-grid cy-grid--3">
            {t('approach.byPillar.columns', { returnObjects: true }).map((col) => (
              <div className="cy-card" key={col.pillar}>
                <h3 className="cy-card-title">{col.pillar}</h3>
                <p className="cy-card-text" style={{ marginTop: 8 }}>
                  <strong style={{ color: 'var(--cy-color-text-primary)' }}>Main input: </strong>{col.input}
                </p>
                <p className="cy-card-text">
                  <strong style={{ color: 'var(--cy-color-text-primary)' }}>Key risk: </strong>{col.risk}
                </p>
                <p className="cy-card-text">
                  <strong style={{ color: 'var(--cy-color-text-primary)' }}>Value focus: </strong>{col.value}
                </p>
                <p className="cy-card-text">
                  <strong style={{ color: 'var(--cy-color-text-primary)' }}>First submission: </strong>{col.material}
                </p>
                <p style={{ marginTop: 16 }}>
                  <Link className="cy-link" to={col.to}>{col.cta}</Link>
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* approach-final-cta */}
      <section className="cy-section cy-section--cta" id="approach-final-cta">
        <div className="cy-container cy-cta-band">
          <h2 className="cy-title" style={{ textAlign: 'center' }}>{t('approach.finalCta.h2')}</h2>
          <p className="cy-lede" style={{ margin: '16px auto 0', textAlign: 'center' }}>{t('approach.finalCta.body')}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center', marginTop: 32 }}>
            <Button to="/submit-opportunity">{t('cta.submit')}</Button>
            <Button to="/contact?topic=partnership" variant="secondary">{t('approach.finalCta.discuss')}</Button>
          </div>
          <p className="cy-fineprint" style={{ marginTop: 24, textAlign: 'center' }}>
            {t('approach.finalCta.privacy')}{' '}
            <Link className="cy-link" to="/privacy">Privacy Policy</Link>
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default Approach;
